/**
 * Decagon in-chat payment drawer (vanilla JS — the drawer must work on every
 * widget-bearing layout, React or not).
 *
 * Flow: a Decagon tool emits the `open_cart` CLIENT_EVENT → the widget partial
 * (app/views/decagon/_widget.html.slim) re-dispatches it on window as
 * 'decagon:open-cart' → this module opens a drawer positioned over the chat
 * panel, tokenizes the card via Braintree HOSTED FIELDS (number / expiry / CVV
 * are Braintree-served iframes, so the PAN never enters this page's DOM —
 * SAQ A posture), and posts the nonce back into the widget iframe via the
 * postMessage bridge (utils/decagonBridge.js):
 *
 *   postMessage({ addMetadata: { payment_nonce, payment_type,
 *     payment_last_four, payment_card_type, billing_first_name,
 *     billing_last_name, billing_country, billing_zip_code } })
 *                                               — metadata, does NOT advance
 *   postMessage({ sdkMessage: "Visa ending in 1111 tokenized" })  (or the
 *     neutral "CreditCard payment method tokenized" when last-4 is unknown)
 *
 * The nonce rides METADATA ONLY — never the message text.
 */
import btClient from 'braintree-web/client'
import btHostedFields from 'braintree-web/hosted-fields'
import { sendNonce, widgetPanelRect, PANEL_W, PANEL_H } from '../utils/decagonBridge'

// The partial is double-rendered on some v5 pages (travel_documents,
// v5/orders/edit render chat/_web again on top of the layout) — bail if
// another copy of this module already owns the drawer.
if (!window.__decagonPaymentDrawer) {
  window.__decagonPaymentDrawer = { open: null, close: null }

  // ---------------------------------------------------------------- config
  const tokenizationKey = () => {
    const el = document.getElementById('decagon-payment-config')
    if (!el) return null
    try {
      return JSON.parse(el.textContent).tokenization_key || null
    } catch (e) {
      return null
    }
  }

  // ---------------------------------------------------------------- drawer
  // Billing country: the deployed tool expects ISO-3166-1 alpha-2 codes
  // (note GB, not UK) — the display name is presentation only.
  const COUNTRIES = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'IE', name: 'Ireland' },
    { code: 'AU', name: 'Australia' },
  ]

  let els = null      // built lazily, once
  let hf = null       // live braintree hosted-fields instance
  let cardReady = false
  let phase = 'idle'  // idle | submitting | sent | error
  let isOpen = false
  let openSeq = 0     // invalidates in-flight async work when the drawer closes
  let repositionTimer = null
  let closeTimer = null
  let lastFocused = null // focus restore target for close (a11y)
  let uid = 0

  const h = (tag, className, attrs) => {
    const el = document.createElement(tag)
    if (className) el.className = className
    if (attrs) Object.keys(attrs).forEach((k) => el.setAttribute(k, attrs[k]))
    return el
  }

  // Wraps a control with its visible label. Plain inputs/selects get an
  // explicit label[for] ↔ id association. Hosted-field containers are DIVs the
  // Braintree iframes mount into — each iframe ships its own accessible title,
  // so the visible label is decorative there (aria-hidden).
  const field = (labelText, control) => {
    const isHfContainer = control.tagName === 'DIV'
    const wrap = h(isHfContainer ? 'div' : 'label', 'dpd-field')
    const span = h('span')
    span.innerHTML = labelText + ' <i>*</i>'
    if (isHfContainer) {
      span.setAttribute('aria-hidden', 'true')
    } else {
      control.id = 'dpd-input-' + (++uid)
      wrap.setAttribute('for', control.id)
    }
    wrap.appendChild(span)
    wrap.appendChild(control)
    return wrap
  }

  const build = () => {
    if (els) return els

    const frame = h('div', 'dpd-frame', { 'aria-hidden': 'true' })
    const backdrop = h('div', 'dpd-backdrop')
    const drawer = h('section', 'dpd-drawer', {
      role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Payment details',
    })
    drawer.appendChild(h('div', 'dpd-grip'))

    const head = h('header', 'dpd-head')
    const sum = h('div', 'dpd-sum')
    const title = h('span', 'dpd-sum-title')
    title.textContent = 'Payment Details'
    const sub = h('span', 'dpd-sum-sub')
    sub.textContent = 'Your card information is encrypted for security'
    sum.appendChild(title)
    sum.appendChild(sub)
    const closeBtn = h('button', 'dpd-close', { type: 'button', 'aria-label': 'Close' })
    closeBtn.textContent = '×'
    head.appendChild(sum)
    head.appendChild(closeBtn)
    drawer.appendChild(head)

    const body = h('div', 'dpd-body')

    const first = h('input', null, { type: 'text', placeholder: 'Enter your first name', autocomplete: 'off' })
    const last = h('input', null, { type: 'text', placeholder: 'Enter your last name', autocomplete: 'off' })
    const row1 = h('div', 'dpd-row')
    row1.appendChild(field('First name', first))
    row1.appendChild(field('Last name', last))
    body.appendChild(row1)

    // Braintree-hosted iframes mount into these — card data never touches
    // this page's DOM.
    const hfNumber = h('div', 'dpd-hf')
    const hfExp = h('div', 'dpd-hf')
    const hfCvv = h('div', 'dpd-hf')
    body.appendChild(field('Card number', hfNumber))
    const row2 = h('div', 'dpd-row')
    row2.appendChild(field('Expiration date', hfExp))
    row2.appendChild(field('CVV', hfCvv))
    body.appendChild(row2)

    const country = h('select')
    COUNTRIES.forEach((c) => {
      const opt = h('option', null, { value: c.code })
      opt.textContent = c.name
      country.appendChild(opt)
    })
    country.value = 'US'
    const zip = h('input', null, { type: 'text', inputmode: 'numeric', placeholder: '12345', autocomplete: 'off' })
    const row3 = h('div', 'dpd-row')
    row3.appendChild(field('Country', country))
    row3.appendChild(field('Billing zip code', zip))
    body.appendChild(row3)

    const submit = h('button', 'dpd-submit', { type: 'button' })
    submit.disabled = true
    body.appendChild(submit)

    const note = h('div', 'dpd-note')
    body.appendChild(note)

    drawer.appendChild(body)
    frame.appendChild(backdrop)
    frame.appendChild(drawer)
    document.body.appendChild(frame)

    els = { frame, backdrop, closeBtn, first, last, hfNumber, hfExp, hfCvv, country, zip, submit, note }

    backdrop.addEventListener('click', close)
    closeBtn.addEventListener('click', close)
    submit.addEventListener('click', onSubmit)
    return els
  }

  const render = () => {
    if (!els) return
    const busy = phase === 'submitting' || phase === 'sent'
    els.submit.disabled = busy || !cardReady
    els.submit.textContent =
      phase === 'submitting' ? 'Submitting…'
        : phase === 'sent' ? 'Sent ✓'
          : cardReady ? 'Submit payment' : 'Loading secure fields…'
    els.note.className = 'dpd-note' + (phase === 'error' ? ' err' : phase === 'sent' ? ' ok' : '')
    if (phase === 'sent') els.note.textContent = 'Payment details sent to chat'
    else if (phase !== 'error') els.note.textContent = ''
  }

  const setError = (e) => {
    phase = 'error'
    if (els) els.note.textContent = (e && e.message) || String(e)
    render()
  }

  const reposition = () => {
    if (!els) return
    const r = widgetPanelRect()
    if (r) {
      els.frame.style.top = r.top + 'px'
      els.frame.style.left = r.left + 'px'
      els.frame.style.right = 'auto'
      els.frame.style.bottom = 'auto'
      els.frame.style.width = r.width + 'px'
      els.frame.style.height = r.height + 'px'
    } else {
      // No widget iframe on screen — dock bottom-right at panel size.
      els.frame.style.top = 'auto'
      els.frame.style.left = 'auto'
      els.frame.style.right = '20px'
      els.frame.style.bottom = '20px'
      els.frame.style.width = PANEL_W + 'px'
      els.frame.style.height = PANEL_H + 'px'
    }
  }

  const onKeydown = (e) => { if (e.key === 'Escape') close() }

  // Teardown matters twice over: Braintree refuses a second create() on a
  // container that still holds a field, and a nonce is single-use — a decline
  // reopens the drawer, which must arrive with FRESH, empty fields.
  const teardownFields = () => {
    cardReady = false
    const instance = hf
    hf = null
    if (instance) instance.teardown().catch(() => {})
  }

  const mountFields = () => {
    const seq = openSeq
    const key = tokenizationKey()
    if (!key) { setError(new Error('Payment is not configured on this page')); return }
    teardownFields()
    btClient.create({ authorization: key })
      .then((client) => btHostedFields.create({
        client: client,
        styles: {
          input: { 'font-size': '14.4px', color: '#12211a' },
          '::placeholder': { color: '#aeb8b2' },
        },
        fields: {
          number: { container: els.hfNumber, placeholder: 'xxxx xxxx xxxx xxxx' },
          expirationDate: { container: els.hfExp, placeholder: 'MM/YY' },
          cvv: { container: els.hfCvv, placeholder: 'xxx' },
        },
      }))
      .then((instance) => {
        if (seq !== openSeq || !isOpen) { instance.teardown().catch(() => {}); return }
        hf = instance
        cardReady = true
        render()
      })
      .catch((e) => { if (seq === openSeq && isOpen) setError(e) })
  }

  const open = () => {
    build()
    openSeq += 1
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null }
    // A re-open without an intervening close (the agent re-emits open_cart
    // after a decline) must not stack a second interval.
    if (repositionTimer) { clearInterval(repositionTimer); repositionTimer = null }
    if (!isOpen) lastFocused = document.activeElement
    isOpen = true
    phase = 'idle'
    els.note.textContent = ''
    reposition()
    els.frame.classList.add('open')
    els.frame.setAttribute('aria-hidden', 'false')
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    document.addEventListener('keydown', onKeydown)
    repositionTimer = setInterval(reposition, 500)
    render()
    mountFields()
    els.first.focus()
  }

  const close = () => {
    if (!els || !isOpen) return
    isOpen = false
    openSeq += 1
    els.frame.classList.remove('open')
    els.frame.setAttribute('aria-hidden', 'true')
    window.removeEventListener('resize', reposition)
    window.removeEventListener('scroll', reposition, true)
    document.removeEventListener('keydown', onKeydown)
    if (repositionTimer) { clearInterval(repositionTimer); repositionTimer = null }
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null }
    teardownFields()
    if (lastFocused && typeof lastFocused.focus === 'function' && document.contains(lastFocused)) {
      lastFocused.focus()
    }
    lastFocused = null
  }

  const onSubmit = () => {
    if (!hf) { setError(new Error('Card fields are still loading — try again in a moment')); return }
    if (phase === 'submitting' || phase === 'sent') return
    const seq = openSeq // closing mid-flight bumps openSeq — see below
    phase = 'submitting'
    render()
    const zip = els.zip.value.trim()
    hf.tokenize(zip ? { billingAddress: { postalCode: zip } } : undefined)
      .then((payload) => {
        // The user cancelled (Escape / backdrop / ×) while tokenize was in
        // flight: an explicit cancel must not post metadata or advance the
        // conversation turn.
        if (seq !== openSeq || !isOpen) return
        if (!payload || !payload.nonce) throw new Error('Braintree returned no nonce')
        if (payload.binData && payload.binData.prepaid === 'Yes') throw new Error('Prepaid cards are not accepted')
        const ok = sendNonce(payload.nonce, 'CreditCard', {
          firstName: els.first.value.trim(),
          lastName: els.last.value.trim(),
          country: els.country.value,
          zip: zip,
        }, {
          lastFour: (payload.details && payload.details.lastFour) || null,
          cardType: (payload.details && payload.details.cardType) || null,
        })
        if (!ok) throw new Error('Chat widget not found — could not send payment details')
        phase = 'sent'
        render()
        closeTimer = setTimeout(close, 1400)
      })
      .catch((e) => { if (seq === openSeq && isOpen) setError(e) })
  }

  window.addEventListener('decagon:open-cart', open)
  window.__decagonPaymentDrawer.open = open
  window.__decagonPaymentDrawer.close = close
}
